import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { GripVertical, X, Plus, ArrowUp, ArrowDown, Smartphone, Eye, AlertTriangle,
  Truck, PackagePlus, Camera, Images, ListChecks, ScanLine, Boxes, Tags, MapPin,
  ShoppingCart, PackageMinus, RotateCcw, ArrowRightLeft, Inbox, Search, Package,
  TrendingUp, FileSpreadsheet, Users, SlidersHorizontal, DatabaseBackup, ScrollText, GitCompareArrows } from "lucide-react";

const QH_ICONS = {
  ScanLine, Truck, PackagePlus, Camera, Images, ListChecks, Boxes, Tags, MapPin,
  ShoppingCart, PackageMinus, RotateCcw, ArrowRightLeft, Inbox, Search, Package,
  TrendingUp, FileSpreadsheet, Users, SlidersHorizontal, DatabaseBackup, ScrollText, GitCompareArrows,
};

// Admin editor for per-role home-screen QUICK-HITS. Additive presentation config on
// top of the permission grid — a button still only shows if the role also holds the
// action's permission. Fixed catalog (v1); ordered; capped.
export default function QuickHitsEditor() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/quick-hits"); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (role, keys) => {
    setBusy(role);
    try {
      await api.put("/admin/quick-hits", { role, keys });
      setData((d) => ({ ...d, quick_hits: { ...d.quick_hits, [role]: keys } }));
      toast.success(`${data.names?.[role] || role} quick-hits saved`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
    finally { setBusy(""); }
  };

  if (!data) return null;
  const catByKey = Object.fromEntries(data.catalog.map((c) => [c.key, c]));
  const label = (k) => (catByKey[k] || {}).label || k;

  return (
    <div data-testid="quick-hits-editor" className="mt-10">
      <div className="flex items-center gap-2">
        <Smartphone className="w-5 h-5 text-[#B01E28]" />
        <h2 className="font-display text-xl font-bold tracking-tight">Home-screen quick-hits</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        A curated set of big home-screen buttons per role (max {data.max}, ordered). A button
        only appears if the person <b>also</b> has permission for it — everything else stays in the menu.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        {data.roles.map((role) => {
          const selected = data.quick_hits[role] || [];
          const remaining = data.catalog.filter((c) => !selected.includes(c.key));
          const full = selected.length >= data.max;
          const move = (i, dir) => {
            const j = i + dir;
            if (j < 0 || j >= selected.length) return;
            const next = [...selected];
            [next[i], next[j]] = [next[j], next[i]];
            save(role, next);
          };
          return (
            <div key={role} data-testid={`qh-role-${role.replace(/\s/g, "-")}`} className="border border-slate-200 bg-white rounded-xl p-4">
              <div className="font-display font-bold text-[#1F1F1F] mb-2">{data.names?.[role] || role}</div>

              <div className="space-y-1.5 mb-3">
                {selected.length === 0 && <div className="text-xs text-slate-400 font-mono py-1">No quick-hits — home shows the menu only.</div>}
                {selected.map((k, i) => (
                  <div key={k} data-testid={`qh-selected-${role.replace(/\s/g, "-")}-${k}`} className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5">
                    <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
                    <span className="font-mono text-[10px] text-slate-400 w-4">{i + 1}</span>
                    <span className="flex-1 text-sm truncate">{label(k)}</span>
                    <button data-testid={`qh-up-${role.replace(/\s/g, "-")}-${k}`} disabled={i === 0 || busy === role} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-[#B01E28] disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button data-testid={`qh-down-${role.replace(/\s/g, "-")}-${k}`} disabled={i === selected.length - 1 || busy === role} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-[#B01E28] disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                    <button data-testid={`qh-remove-${role.replace(/\s/g, "-")}-${k}`} disabled={busy === role} onClick={() => save(role, selected.filter((x) => x !== k))} className="p-1 text-slate-400 hover:text-[#C62828] disabled:opacity-30"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>

              {remaining.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {remaining.map((c) => (
                    <button key={c.key} data-testid={`qh-add-${role.replace(/\s/g, "-")}-${c.key}`}
                      disabled={full || busy === role}
                      onClick={() => save(role, [...selected, c.key])}
                      className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-full px-2.5 py-1 text-slate-600 hover:border-[#E0781A] hover:text-[#E0781A] disabled:opacity-40 disabled:cursor-not-allowed">
                      <Plus className="w-3 h-3" />{c.label}
                    </button>
                  ))}
                </div>
              )}
              {full && <div className="text-[11px] text-amber-600 mt-2 font-mono">Max {data.max} reached — remove one to add another.</div>}

              {(() => {
                const granted = data.granted?.[role] || [];
                const shown = selected.filter((k) => granted.includes(k));
                const hidden = selected.filter((k) => !granted.includes(k));
                return (
                  <div data-testid={`qh-preview-${role.replace(/\s/g, "-")}`} className="mt-3 pt-3 border-t border-dashed border-slate-200">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Eye className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-[11px] font-mono uppercase tracking-wide text-slate-400">Home preview — what {role} actually sees</span>
                    </div>
                    {shown.length === 0 ? (
                      <div className="text-xs text-slate-400 font-mono">No tiles — home shows the menu only.</div>
                    ) : (
                      <div className="grid grid-cols-3 gap-1.5">
                        {shown.map((k) => {
                          const c = catByKey[k]; const Icon = QH_ICONS[c?.icon] || Eye;
                          return (
                            <div key={k} data-testid={`qh-preview-tile-${role.replace(/\s/g, "-")}-${k}`}
                              className="flex flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-2 text-center">
                              <Icon className="w-4 h-4 text-[#B01E28]" />
                              <span className="text-[9px] leading-tight text-slate-600 line-clamp-2">{c?.label || k}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {hidden.length > 0 && (
                      <div data-testid={`qh-preview-hidden-${role.replace(/\s/g, "-")}`} className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>Hidden for {role} (role lacks permission): {hidden.map(label).join(", ")}</span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
